import classNames from "classnames";
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ViewProps } from "../ExecutiveDashboard";
import Hint from "../components/Hint";
import { Card, Empty, ErrorState, Kpi, Loading, PendingTag } from "../components/Visuals";
import { addAreas, area, bothAreas, date, daysFromToday, LEAD_UNIT, NEXT_STEP, number, otherUnit, plural, SCHEDULE_LABELS } from "../format";
import { AlsoInStageResponse, AlsoInStageRow, Area, DashboardProperty, UnitRow, UnitsResponse } from "../types";
import { useDashboardData } from "../useDashboardData";
import { Select } from "./Overview";
import UnitDrawer from "./UnitDrawer";

type Stage = "design" | "fitout";

const STAGES: { [key in Stage]: { label: string; rule: string } } = {
	design: {
		label: "Design Stage",
		rule: "Leases signed but not open, before the Actual Move in Date on the Site Handover task."
	},
	fitout: {
		label: "Fitout Stage",
		rule: "Leases signed but not open, on or after the Actual Move in Date on the Site Handover task."
	}
};

interface Group {
	key: string;
	units: UnitRow[];
	first: UnitRow;
}

interface Props extends ViewProps {
	stage: Stage;
}

const StageUnits = ({ stage, asset, properties }: Props) => {
	const { data, isLoading, error } = useDashboardData<UnitsResponse>("units", { property_id: asset ? asset.id : undefined });
	const extras = useDashboardData<AlsoInStageResponse>("also_in_stage", { property_id: asset ? asset.id : undefined }, { background: true });
	const [query, setQuery] = useState("");
	const [property, setProperty] = useState("");
	const [openUnit, setOpenUnit] = useState<string | null>(null);

	if (isLoading) return <Loading />;
	if (error || !data) return <ErrorState />;

	const config = STAGES[stage];
	const inStage = data.units.filter((unit) => unit.status === stage && (!property || unit.property.id === property));
	const groups = groupByLease(inStage);
	const needle = query.trim().toLowerCase();
	const counted = groups.map(fromGroup);
	const notCounted = (extras.data ? extras.data.projects : [])
		.filter((row) => row.stage === stage && (!property || (row.property && row.property.id === property)))
		.map(fromExtra);
	const lines = [...counted, ...notCounted]
		.filter((line) => {
			if (!needle) return true;
			return [line.tenant, line.units.map((unit) => unit.code).join(" "), line.property && line.property.short_name, line.property && line.property.name, line.zone, line.subZone]
				.join(" ")
				.toLowerCase()
				.includes(needle);
		})
		.sort(byTargetOpen);
	const shownCounted = lines.filter((line) => !line.notCounted);
	const shownNotCounted = lines.length - shownCounted.length;

	const totalGla = addAreas(inStage.map((unit) => unit.gla));
	const pending = groups.filter((group) => group.first.project_pending).length;
	const opening90 = groups.filter((group) => {
		const days = daysFromToday(group.first.target_open_date, data.as_of);
		return days !== null && days >= 0 && days <= 90;
	}).length;

	return (
		<div className="ed-stack">
			<div className="ed-kpis">
				<Kpi
					label="Leases"
					value={number(groups.length)}
					valueTone={stage}
					sub={`(${plural(inStage.length, "unit")})`}
					explain={{
						title: config.label,
						lines: [config.rule, NEXT_STEP[stage], "Counted by lease: a lease over several units counts once.", "The same figure as on the Overview."]
					}}
				/>
				<Kpi
					label="GLA"
					value={area(totalGla)}
					sub={area(totalGla, otherUnit(LEAD_UNIT))}
					explain={{ title: `${config.label} GLA`, lines: ["Lease area of the units in this stage.", bothAreas(totalGla)] }}
				/>
				<Kpi
					label="Pending projects"
					dot="refurb"
					value={number(pending)}
					explain={{ title: "Pending projects", lines: ["Leases whose project has been created from Yardi but not scheduled yet."] }}
				/>
				<Kpi
					label="Opening in 90 days"
					dot="trading"
					value={number(opening90)}
					explain={{ title: "Opening in 90 days", lines: ["Leases with a target opening date in the next 90 days."] }}
				/>
			</div>

			<div className="ed-toolbar ed-toolbar--wrap">
				<input
					className="ed-search"
					type="search"
					placeholder="Search tenant, unit, zone…"
					value={query}
					onChange={(event) => setQuery(event.target.value)}
				/>
				{!asset && (
					<Select
						label="Property"
						value={property}
						options={properties.map((item) => item.id)}
						format={(id) => {
							const match = properties.find((item) => item.id === id);
							return match ? match.short_name : id;
						}}
						onChange={setProperty}
					/>
				)}
			</div>

			<Card
				title={config.label}
				subtitle={`${plural(shownCounted.length, "lease")} · ${plural(
					shownCounted.reduce((sum, line) => sum + line.units.length, 0),
					"unit"
				)}${shownNotCounted ? ` · ${number(shownNotCounted)} more not counted` : ""}${
					extras.isLoading ? " · checking for projects not counted…" : ""
				}, soonest target opening first`}
				className="ed-card--flush"
			>
				{lines.length === 0 ? (
					<Empty>No leases in this stage match.</Empty>
				) : (
					<div className="ed-table-wrap">
						<table className="ed-table">
							<thead>
								<tr>
									<th className="ed-num ed-col--index">#</th>
									<th>Tenant</th>
									{!asset && <th>Property</th>}
									<th>Units</th>
									<th>Zone</th>
									<th className="ed-num">GLA</th>
									<th>Move-in</th>
									<th>Target open</th>
								</tr>
							</thead>
							<tbody>
								{lines.map((line, index) => (
									<tr key={line.key} className={classNames({ "ed-row--muted": line.notCounted })}>
										<td className="ed-num ed-col--index">{index + 1}</td>
										<td className="ed-strong">
											{line.projectId ? (
												<Link to={`/projects/${line.projectId}`} target="_blank" rel="noopener noreferrer" className="ed-link" title="Open project in a new tab">
													{line.tenant || "Unnamed tenant"}
												</Link>
											) : (
												line.tenant || "Unnamed tenant"
											)}
											{line.pending && <PendingTag />}
											{line.notCounted && (
												<Hint explain={{ title: "Not counted", lines: [line.notCounted, `In ${config.label}, but not in the figures above or on the Overview.`] }}>
													<span className="ed-uncounted">Not counted</span>
												</Hint>
											)}
										</td>
										{!asset && <td>{line.property ? line.property.short_name : "–"}</td>}
										<td>
											{line.units.length === 0 && "–"}
											{line.units.map((unit, position) => (
												<React.Fragment key={unit.id}>
													{position > 0 && ", "}
													<button type="button" className="ed-unit-link" onClick={() => setOpenUnit(unit.id)} title="Show lease history">
														{unit.code}
													</button>
												</React.Fragment>
											))}
										</td>
										<td>
											{line.zone || "–"}
											{line.subZone && <div className="ed-subtext">{line.subZone}</div>}
										</td>
										<td className="ed-num">
											<Hint align="right" explain={{ lines: [bothAreas(line.gla), `${plural(line.units.length, "unit")} on this lease.`] }}>
												{area(line.gla)}
												<div className="ed-subtext">{area(line.gla, otherUnit(LEAD_UNIT))}</div>
											</Hint>
										</td>
										<td>
											<Hint
												explain={{
													lines: [
														line.moveInActual
															? "Actual Move in Date recorded on the Site Handover task."
															: "Agreed Site Handover date (Actual Move in Date not recorded yet).",
														NEXT_STEP[stage]
													]
												}}
											>
												{date(line.moveIn)}
												<div className="ed-subtext">{line.moveInActual ? "Actual" : "Agreed"}</div>
											</Hint>
										</td>
										<td>{date(line.targetOpen)}</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}
			</Card>
			{openUnit && <UnitDrawer unitId={openUnit} onClose={() => setOpenUnit(null)} />}
		</div>
	);
};

interface Line {
	key: string;
	projectId: string | null;
	pending: boolean;
	tenant: string | null;
	property: DashboardProperty | null;
	units: { id: string; code: string }[];
	zone: string | null;
	subZone: string | null;
	gla: Area;
	moveIn: string | null;
	moveInActual: boolean;
	targetOpen: string | null;
	notCounted: string | null;
}

const fromGroup = (group: Group): Line => ({
	key: group.key,
	projectId: group.first.project_id,
	pending: group.first.project_pending,
	tenant: group.first.tenant_name,
	property: group.first.property,
	units: group.units.map((unit) => ({ id: unit.id, code: unit.code })),
	zone: group.first.zone,
	subZone: group.first.sub_zone,
	gla: addAreas(group.units.map((unit) => unit.gla)),
	moveIn: group.first.move_in_date || null,
	moveInActual: !!group.first.move_in_actual,
	targetOpen: group.first.target_open_date,
	notCounted: null
});

const fromExtra = (row: AlsoInStageRow): Line => ({
	key: `extra:${row.key}`,
	projectId: row.project_id,
	pending: row.pending,
	tenant: row.tenant_name,
	property: row.property,
	units: row.units,
	zone: row.zone,
	subZone: row.sub_zone,
	gla: row.gla,
	moveIn: row.move_in_date,
	moveInActual: row.move_in_actual,
	targetOpen: row.target_open_date,
	notCounted: notCountedReason(row)
});

const notCountedReason = (row: AlsoInStageRow) => {
	const label = row.kind === "works" ? row.type_label : row.kind === "current" ? "Opening project on the current lease" : "New lease";
	const holder = row.holder;
	if (row.units.length === 0) return `${label}: no units linked to the project.`;
	if (!holder) return `${label}: ${row.kind === "works" ? "no lease holds the unit today" : "not on a signed lease term yet"}.`;
	const status = SCHEDULE_LABELS[holder.status];
	if (row.kind === "works") return `${label}: the unit counts as ${status}${holder.tenant_name ? ` under ${holder.tenant_name}` : ""}.`;
	if (row.kind === "current") return `${label}: the unit counts as ${status} under that lease.`;
	const until = holder.until ? ` until ${date(holder.until)}` : "";
	if (holder.tenant_name && holder.tenant_name === row.tenant_name) {
		return `${label} for ${row.tenant_name}, replacing their current lease, which holds the unit${until}.`;
	}
	return `${label}: ${holder.tenant_name || "another lease"} holds the unit${until}.`;
};

const groupByLease = (units: UnitRow[]): Group[] => {
	const groups: { [key: string]: UnitRow[] } = {};
	units.forEach((unit) => {
		const key = unit.lease_key || unit.id;
		groups[key] = groups[key] || [];
		groups[key].push(unit);
	});
	return Object.keys(groups).map((key) => {
		const sorted = groups[key].sort((a, b) => a.code.localeCompare(b.code, undefined, { numeric: true }));
		return { key, units: sorted, first: sorted[0] };
	});
};

const byTargetOpen = (a: Line, b: Line) =>
	(a.targetOpen || "9999").localeCompare(b.targetOpen || "9999") || (a.tenant || "").localeCompare(b.tenant || "");

export default StageUnits;
